import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, DatePicker, Drawer } from 'antd';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import timezone from 'dayjs/plugin/timezone';
import useScreenType from '../../hooks/context/useScreenType';
import useHelpers from '../../hooks/context/useHelpers';
import useForm from '../../hooks/utility/useForm';
import useApiClient from '../../hooks/api/useApiClient';
import { useUpdateDeliveryMutation } from '../../store/api-slices';

dayjs.extend(timezone);
// The picker is an editor, and editors stay on a 24h clock across the dashboard
// (opening hours, schedule exceptions, offers) — an unambiguous field to type into.
// Only the confirmed time above it is a display, so only that one follows the
// location's 12h/24h preference.
const editorFormat = 'YYYY-MM-DD HH:mm';

export default function OrderDeliveryDetailsModal({ show = false, toggleModal, data }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Modals:Order:OrderDeliveryDetailsModal' });
  const restaurantName = useSelector((state) => state.currentCompany.company.name);
  const { isDesktop } = useScreenType();
  const { formatDateTime, timezone: restaurantTimezone } = useHelpers();
  const dropoffEta = restaurantTimezone
    ? dayjs(data?.delivery.dropoff_estimate).tz(restaurantTimezone)
    : dayjs(data?.delivery.dropoff_estimate);
  const { DeliveriesManager } = useApiClient();
  const { data: formData, update } = useForm({
    dropoff_ready: '',
  });
  const [updateDelivery, { isLoading }] = useUpdateDeliveryMutation();
  const save = () =>
    updateDelivery({
      apiManager: DeliveriesManager,
      id: data.delivery.id,
      data: formData,
    }).then((res) => {
      if (res.error) {
        toast.error(t('toasts.failed_to_save'));

        return;
      }

      toast.success(t('toasts.success'));
      toggleModal();
    });

  return (
    <Drawer
      id="order-delivery-details-modal"
      open={show}
      alignment="center"
      scrollable
      size={window.innerWidth * (isDesktop ? 0.5 : 1)}
      onClose={toggleModal}
      title={t('title')}
      classNames={{
        body: '!overflow-x-hidden',
      }}
    >
      <section className="flex flex-col h-full gap-6">
        <Alert
          description={t('alerts.dropoff_time_info', { timezone: restaurantTimezone, restaurantName })}
          type="warning"
          showIcon
        />
        <fieldset className="flex flex-col gap-3">
          <legend className="text-base font-medium">{t('labels.dropoff_time')}</legend>
          <p>
            {t('labels.confirmed')}
            <span className="ml-2">{formatDateTime(data?.delivery.dropoff_estimate)}</span>
          </p>
          <p>{t('labels.new_time')}</p>
          <DatePicker
            showTime
            format={editorFormat}
            defaultValue={dropoffEta}
            onChange={(date) => update({ dropoff_ready: date.toISOString() })}
          />
        </fieldset>
        <Button className="mt-auto" type="primary" onClick={save} loading={isLoading}>
          {t('buttons.save')}
        </Button>
      </section>
    </Drawer>
  );
}
